<script setup lang="ts">
interface Props {
  text?: string | null
  emptyText?: string
  tooltipMaxWidth?: number
  icon?: string | null
  iconColor?: string | null
  iconSize?: string | number
  iconClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  text: null,
  emptyText: '-',
  tooltipMaxWidth: 420,
  icon: null,
  iconColor: null,
  iconSize: 22,
  iconClass: '',
})

const textRef = ref<HTMLElement | null>(null)
const cellRef = ref<HTMLElement | null>(null)
const isTruncated = ref(false)

let resizeObserver: ResizeObserver | null = null

const displayText = computed(() => {
  const value = props.text?.toString() || ''
  return value || props.emptyText
})

const updateTruncation = () => {
  const textElement = textRef.value
  if (!textElement) {
    isTruncated.value = false
    return
  }

  isTruncated.value = textElement.scrollWidth > textElement.clientWidth + 1
}

const observeCell = () => {
  resizeObserver?.disconnect()

  if (!cellRef.value || typeof ResizeObserver === 'undefined') {
    updateTruncation()
    return
  }

  resizeObserver = new ResizeObserver(() => {
    updateTruncation()
  })
  resizeObserver.observe(cellRef.value)
}

onMounted(() => {
  nextTick(() => {
    updateTruncation()
    observeCell()
  })
})

watch(displayText, () => {
  nextTick(updateTruncation)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<template>
  <div
    ref="cellRef"
    class="project-files-truncated-text-cell"
  >
    <VIcon
      v-if="icon"
      :icon="icon"
      :color="iconColor || undefined"
      :size="iconSize"
      :class="['project-files-truncated-text-cell__icon', iconClass]"
    />

    <ProjectFilesItemTooltip
      :content="isTruncated ? displayText : ''"
      :max-width="tooltipMaxWidth"
      v-slot="{ triggerProps }"
    >
      <span
        ref="textRef"
        v-bind="triggerProps"
        class="project-files-truncated-text-cell__text"
      >
        {{ displayText }}
      </span>
    </ProjectFilesItemTooltip>
  </div>
</template>

<style scoped lang="scss">
.project-files-truncated-text-cell {
  display: flex;
  align-items: center;
  min-inline-size: 0;
  inline-size: 100%;
}

.project-files-truncated-text-cell__icon {
  flex: 0 0 auto;
}

.project-files-truncated-text-cell__text {
  display: block;
  flex: 1 1 auto;
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
